import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CONVERSATIONS = [
  {
    messages: [
      { role: 'user', text: "Can you move my dentist to next week?" },
      { role: 'claw', text: "Done! Moved to Tuesday the 18th at 2PM. I've updated your calendar and set a reminder for the night before 🦷" },
    ],
  },
  {
    messages: [
      { role: 'user', text: "What do I have on this weekend?" },
      { role: 'claw', text: "Saturday: brunch with Mia at 11AM, then free. Sunday: nothing booked — perfect for that hike you mentioned 🌿" },
    ],
  },
  {
    messages: [
      { role: 'user', text: "Remind me to call mum on Sunday evening" },
      { role: 'claw', text: "Set! I'll remind you Sunday at 6PM. Want me to add a note so you remember to ask about her trip? ✨" },
    ],
  },
  {
    messages: [
      { role: 'user', text: "Find me a flight to Lisbon in October" },
      { role: 'claw', text: "Looking now… Found 3 options from £189 return. Cheapest departs Oct 9th. Want me to send the links to your email? ✈️" },
    ],
  },
];

const ChatPreview = () => {
  const [convoIdx, setConvoIdx] = useState(0);
  const [step, setStep] = useState(0); 
  const [displayedUserText, setDisplayedUserText] = useState('');
  const chatBodyRef = useRef(null);

  const convo = CONVERSATIONS[convoIdx];
  const fullUserText = convo.messages[0].text;

  useEffect(() => {
    let timeout;
    if (step === 0) {
      if (displayedUserText.length < fullUserText.length) {
        timeout = setTimeout(() => {
          setDisplayedUserText(fullUserText.slice(0, displayedUserText.length + 1));
        }, 30); 
      } else {
        timeout = setTimeout(() => setStep(1), 600); 
      }
    } else if (step === 1) {
      timeout = setTimeout(() => setStep(2), 2200);
    } else if (step === 2) {
      timeout = setTimeout(() => setStep(3), 3500); 
    } else if (step === 3) {
      timeout = setTimeout(() => {
        setDisplayedUserText('');
        setConvoIdx(i => (i + 1) % CONVERSATIONS.length);
        setStep(0);
      }, 400); 
    }
    return () => clearTimeout(timeout);
  }, [step, displayedUserText, fullUserText, convoIdx]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: step === 0 ? 'auto' : 'smooth',
      });
    }
  }, [step, displayedUserText]);

  return (
    <div className="bg-white border border-[#E8E2DA] rounded-2xl shadow-lg overflow-hidden w-full max-w-[280px] mx-auto flex flex-col" style={{ height: '300px' }}>
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-[#EDE9E4] flex items-center gap-3 bg-white">
        <div className="relative">
          <div className="w-7 h-7 rounded-xl bg-[#1A1A18] flex items-center justify-center text-sm">🦞</div>
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[#3D7A5E] border-2 border-white rounded-full" />
        </div>
        <div>
          <p className="text-[12px] font-semibold text-[#1A1A18] leading-tight">Claw</p>
          <p className="text-[9px] text-[#9E9690] font-mono-custom mt-0.5">Your personal assistant</p>
        </div>
      </div>

      <div ref={chatBodyRef} className="flex-1 overflow-y-auto px-3 py-3 bg-[#F8F6F3]/70 space-y-3 flex flex-col justify-end" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <AnimatePresence>
          {step < 3 && (
            <motion.div key={`user-${convoIdx}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} className="flex justify-end w-full">
              <div className="chat-bubble-user px-3 py-2 text-[12px] max-w-[80%]">
                {displayedUserText}
                {step === 0 && displayedUserText.length < fullUserText.length && (
                  <span className="inline-block w-[3px] h-[11px] bg-white/70 ml-1 mb-[-1px] animate-pulse rounded-sm" />
                )}
              </div>
            </motion.div>
          )}
          {step === 1 && (
            <motion.div key="typing" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.3 } }} className="flex items-center gap-2 w-full">
              <span className="text-sm">🦞</span>
              <div className="chat-bubble-claw px-3 py-2 flex items-center gap-1 h-8">
                {[0, 0.2, 0.4].map((d, i) => (
                  <motion.div key={i} animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.7, delay: d }} className="w-1.5 h-1.5 bg-[#9E9690] rounded-full" />
                ))}
              </div>
            </motion.div>
          )}
          {step === 2 && (
            <motion.div key={`claw-${convoIdx}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.2 } }} className="flex items-start gap-2 w-full">
              <span className="text-sm flex-shrink-0 mt-0.5">🦞</span>
              <div className="chat-bubble-claw px-3 py-2 text-[12px] text-[#1A1A18] max-w-[82%] leading-relaxed">
                {convo.messages[1].text}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex-shrink-0 px-3 py-2.5 border-t border-[#EDE9E4] bg-white flex items-center gap-2">
        <div className="flex-1 bg-[#F8F6F3] border border-[#E8E2DA] rounded-full px-3 py-1.5 text-[11px] text-[#9E9690]">
          Message Claw…
        </div>
        <div className="w-6 h-6 rounded-full bg-[#1A1A18] flex items-center justify-center text-[10px] text-white">↑</div>
      </div>
    </div>
  );
};

export default ChatPreview;